import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface RoomBarGroupProps {
  label: string;
  variant?: 'session' | 'tips' | 'more';
  children: ReactNode;
}

export function RoomBarGroup({
  label,
  variant = 'session',
  children,
}: RoomBarGroupProps): ReactElement {
  const [open, setOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node) || !groupRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`room-bar-group room-bar-group--${variant}${open ? ' is-open' : ''}`}
      ref={groupRef}
    >
      <button
        type="button"
        className="room-bar-group__trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {label}
      </button>
      <div
        className="room-bar-group__items"
        onClick={
          variant === 'more'
            ? () => {
                setOpen(false);
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}
